/**
 * Learner Readiness Score HUD indicator.
 *
 * 5-bar phone-signal-style icon, rendered alongside the other session-player
 * chrome icons (keyboard, sound, X). See docs/fatigue-score/README.md §10.1.
 *
 * Behaviour:
 *  - Cold-start: bars rendered at full intensity (the score IS real — it's
 *    computed against population baselines per §6.5) with a "Still
 *    personalising" caveat in the tooltip. Originally we rendered the bars
 *    muted to signal "this isn't your real reading yet"; in practice the
 *    muted opacity made the bars effectively invisible (0.4 button × 0.5
 *    bar = 0.2 effective) and looked like the widget was broken.
 *  - Active: bars filled by score → bucket mapping, with state-not-component
 *    tooltip messaging (no mention of speed/accuracy/variability — see §6.1)
 *  - Loading: rendered muted; no tooltip until data lands
 *
 * The icon is always visible during a session — that's intentional per §10.1.
 * A small always-on indicator + a tooltip on hover gives the user calibration
 * without nagging them.
 *
 * Tooltip context (algo v1.8.0):
 *  - "X min of learning since HH:MM" — episode-scoped. X is the active learning
 *    time across all sessions in the user's current learning episode (summed
 *    from step_time entries; excludes idle gaps and tab-away time). HH:MM is
 *    when the episode started.
 *
 *  Episode-scoped (not session-scoped) on purpose: a learner doing two lessons
 *  back-to-back in one sitting wants to see total engaged time for the sitting,
 *  not a counter that resets each lesson. The 60-min gap threshold + stale
 *  episode reset keep "this sitting" bounded — episodes can be at most a few
 *  hours old in practice, and stale ones get auto-closed on the next submit
 *  via `refreshStaleEpisodeForSession`.
 *
 *  Both values come from `useMyReadiness`'s dashboard response (polled every
 *  60s while the indicator is mounted). The dashboard endpoint resolves the
 *  active episode at request time — no frozen anchors. Step submits also
 *  refresh the same values via `inSessionReadiness` so the tooltip stays
 *  in sync with what the algorithm just computed.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useMyReadiness } from '@/shared/api/hooks/useReadiness';
import { useSession } from '../SessionContext';

const BAR_HEIGHTS = [4, 7, 10, 13, 16] as const; // px, ascending — phone-signal shape

interface StateMessage {
  primary: string;
  /** Optional secondary line in the tooltip — used for the cold-start caveat. */
  secondary?: string;
}

function getStateMessage(opts: { isColdStart: boolean; bars: number }): StateMessage {
  // No "Loading…" state. Bars always default to 5 (defensible "fresh" assumption)
  // until real data arrives, so the tooltip is always backed by a real bars value.
  const barsMessage = getBarsMessage(opts.bars);
  if (opts.isColdStart) {
    return {
      primary: barsMessage,
      secondary: 'Still personalising — your reading sharpens as you learn',
    };
  }
  return { primary: barsMessage };
}

/**
 * Format a millisecond-epoch timestamp as a local "HH:MM" using the user's
 * locale. Empty string if invalid — caller suppresses the line.
 */
function formatStartedAt(ms: number | null | undefined): string {
  if (ms == null) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Build the "X min of learning since HH:MM" tooltip line, or null when there's
 * nothing meaningful to show.
 *
 * Round-UP semantics (`Math.ceil`) with a floor of 1 once any positive active
 * time exists — saying "0 min of learning" the moment a learner opens a step
 * looks like the widget is broken. Sub-minute durations round to 1, larger
 * fractions to the next whole minute. The user sees "1 min" the instant they
 * begin and "2 min" after the first 60s of active time accrues.
 *
 * Returns null when there's no episode (`episodeStartedAtMs == null`) or when
 * active minutes is null/non-positive — caller hides the line entirely.
 */
function buildSessionContextLine(
  activeMinutesInEpisode: number | null | undefined,
  episodeStartedAtMs: number | null | undefined
): string | null {
  if (activeMinutesInEpisode == null || activeMinutesInEpisode <= 0) return null;
  if (episodeStartedAtMs == null) return null;
  const startedAt = formatStartedAt(episodeStartedAtMs);
  if (!startedAt) return null;
  const minutes = Math.max(1, Math.ceil(activeMinutesInEpisode));
  return `${minutes} min of learning since ${startedAt}`;
}

/**
 * Compute a trend direction from the readiness score history (algo v1.9.0).
 *
 * Returns null until at least 3 data points exist — fewer than 3 submits is
 * too noisy to call a trend. Slope is `(last - first) / (n - 1)` over the
 * buffered history (capped at 5 by the reducer). Dead-zone of ±0.04 per
 * submit prevents flicker around small movements.
 *
 * UX intent: this is a more responsive signal than bars during the small-
 * window phase. Bars are slow-moving by design (hysteresis + trust blend);
 * the arrow shows the underlying direction of the displayed score so a
 * struggling learner sees ▼ as soon as they start struggling, not after
 * they've crossed a bar boundary.
 */
type TrendDirection = 'up' | 'flat' | 'down';
const TREND_SLOPE_THRESHOLD = 0.04;
function computeTrend(history: readonly number[]): TrendDirection | null {
  if (history.length < 3) return null;
  const first = history[0];
  const last = history[history.length - 1];
  if (first == null || last == null) return null;
  const slope = (last - first) / (history.length - 1);
  if (slope > TREND_SLOPE_THRESHOLD) return 'up';
  if (slope < -TREND_SLOPE_THRESHOLD) return 'down';
  return 'flat';
}

function getBarsMessage(bars: number): string {
  switch (bars) {
    case 5:
      return "You're learning at your peak";
    case 4:
      return 'Focus is strong';
    case 3:
      return "You're settled in";
    case 2:
      return 'Focus is dipping a little — a break could help';
    case 1:
    default:
      return "You've been learning hard — time for a break";
  }
}

interface ReadinessIndicatorProps {
  size?: number;
}

/**
 * Read the SessionContext defensively — `useSession()` throws if there's no
 * provider in scope. The HUD is rendered inside SessionToolbar which only ever
 * mounts within the session player, so the provider IS in scope today, but
 * making the read defensive future-proofs the indicator for use on the
 * dashboard / shell where there's no session.
 */
function useSafeSession() {
  try {
    return useSession().state;
  } catch {
    return null;
  }
}

export function ReadinessIndicator({ size = 20 }: ReadinessIndicatorProps) {
  const session = useSafeSession();
  const inSessionReadiness = session?.readiness;

  // /me/readiness drives the bars BEFORE the user has submitted any steps in
  // this session — so opening a session after a long break correctly shows
  // the recovered score immediately at load, not a muted/empty state.
  // Once an in-session reading lands (first step submit), it takes over.
  const { data } = useMyReadiness();
  const [hovered, setHovered] = useState(false);

  const bars = inSessionReadiness?.bars ?? data?.bars ?? 5;
  const isColdStart = inSessionReadiness?.isColdStart ?? data?.isColdStart ?? false;
  // Never visually mute — bars default to 5 ("fresh" assumption) until real
  // data arrives. The "Loading…" tooltip state is gone for the same reason:
  // we always have a bars value to back the state message.
  const muted = false;

  const state = getStateMessage({ isColdStart, bars });

  // Episode-scoped context line (algo v1.8.0): "X min of learning since HH:MM".
  //
  // Prefers the dashboard-endpoint value (`data`), which is refreshed every 60s
  // and reflects fresh episode state including stale-episode detection. Falls
  // back to the last in-session readiness when the dashboard hasn't loaded yet
  // — both fields carry the same semantics (sum of step_time across all
  // sessions in the current episode), so taking the more-recently-arrived
  // value is safe.
  //
  // We use `??` (not `||`) so a legitimate `0` from the server doesn't get
  // shadowed by a stale `inSessionReadiness` value.
  const activeMinutesInEpisode =
    data?.activeMinutesInEpisode ?? inSessionReadiness?.activeMinutesInEpisode ?? null;
  const episodeStartedAtMs =
    data?.episodeStartedAtMs ?? inSessionReadiness?.episodeStartedAtMs ?? null;
  const sessionContextLine = buildSessionContextLine(activeMinutesInEpisode, episodeStartedAtMs);

  // Algo v1.9.0 — trend arrow alongside the bars. Shown only when we have
  // enough history to call a direction (≥3 submits). Lives next to the bars
  // for now during the debugging phase; if it proves more useful than the
  // bars themselves, the bars can be retired later.
  const trend = computeTrend(session?.readinessHistory ?? []);

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <button
        type="button"
        className="flex items-center justify-center rounded-lg p-2 opacity-40 transition-opacity hover:opacity-80 cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        aria-label={`Learning readiness: ${bars} of 5 bars`}
        style={{ color: 'var(--f-fg)' }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          {BAR_HEIGHTS.map((h, i) => {
            // Per-bar opacities are picked so the FINAL visual weight matches
            // the keyboard / sound / X icons (which use opacity-40 at rest,
            // hover:opacity-80). The parent button supplies the rest/hover
            // multiplier; these values control the within-icon active/inactive
            // contrast.
            const isActive = !muted && i < bars;
            const barOpacity = muted ? 0.5 : isActive ? 1 : 0.35;
            const x = 1 + i * 4;
            const y = 18 - h;
            return <rect key={i} x={x} y={y} width={2.5} height={h} rx={1} opacity={barOpacity} />;
          })}
        </svg>
      </button>
      {trend && (
        <span
          className="flex items-center opacity-40 transition-opacity hover:opacity-80"
          style={{ color: 'var(--f-fg)' }}
          aria-label={`Readiness trend: ${trend}`}
          title={
            trend === 'up'
              ? 'Recovering'
              : trend === 'down'
                ? 'Declining'
                : 'Steady'
          }
        >
          {trend === 'up' ? (
            <TrendingUp size={size - 4} aria-hidden="true" />
          ) : trend === 'down' ? (
            <TrendingDown size={size - 4} aria-hidden="true" />
          ) : (
            <Minus size={size - 4} aria-hidden="true" />
          )}
        </span>
      )}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0, y: -2 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -2 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full z-50 mt-1 w-64 rounded-md p-2 text-xs shadow-lg"
            style={{
              background: 'var(--f-bg)',
              color: 'var(--f-fg)',
              border: '1px solid var(--f-border, rgba(0,0,0,0.1))',
            }}
            role="tooltip"
          >
            <div>{state.primary}</div>
            {state.secondary && <div className="mt-1 opacity-60">{state.secondary}</div>}
            {sessionContextLine && <div className="mt-1 opacity-60">{sessionContextLine}</div>}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
